import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { ORACLE } from "@/constants/testIds";
import { Sparkles, Bookmark } from "lucide-react";
import { useSoulProfile } from "@/lib/useSoulProfile";
import { houseFor } from "@/lib/houses";
import NarratedText from "@/components/NarratedText";
import { useNarrator } from "@/lib/narrator";
import { playAudioLogo } from "@/lib/audioLogo";

export default function Oracle() {
  const navigate = useNavigate();
  const [moon, setMoon] = useState(null);
  const [spreads, setSpreads] = useState([]);
  const [reading, setReading] = useState(null);
  const [loadingReading, setLoadingReading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const { element } = useSoulProfile();
  const house = houseFor(element);
  const { muted } = useNarrator();

  useEffect(() => {
    api.get("/oracle/moon-phase").then((r) => setMoon(r.data)).catch(() => {});
    api.get("/oracle/spreads").then((r) => setSpreads(r.data.spreads || [])).catch(() => {});
  }, []);

  const loadCardOfDay = async () => {
    setLoadingReading(true);
    setError("");
    setSaved(false);
    setFlipped(false);
    try {
      const { data } = await api.get("/oracle/card-of-day");
      setReading(data);
      // Auto-flip after a small pause so the House card back is visible first
      setTimeout(() => {
        setFlipped(true);
        playAudioLogo({ muted });
      }, 1600);
    } catch (e) {
      setError(e?.response?.data?.detail || "The card refused to be drawn.");
    } finally {
      setLoadingReading(false);
    }
  };

  const saveToJournal = async () => {
    if (!reading) return;
    try {
      await api.post("/journal/save", {
        kind: "card_of_day",
        title: `${reading.card.title} · ${reading.card.upright ? "Upright" : "Reversed"}`,
        date: reading.date,
        moon_phase: reading.moon?.phase_name,
        cards: [reading.card],
        narrative: {
          signal: reading.signal,
          movement: reading.movement,
          blessing: reading.blessing,
        },
      });
      setSaved(true);
      toast.success("Kept in your journal.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
  };

  useEffect(() => {
    loadCardOfDay();
  }, []);

  return (
    <div data-testid={ORACLE.home} className="relative min-h-screen bg-[#080808] px-6 sm:px-12 lg:px-20 py-12 lg:py-20 pb-32 lg:pb-20">
      <div className="starfield-subtle" />

      <div className="relative max-w-6xl mx-auto">
        {/* Header row */}
        <div className="flex items-start justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="font-overline mb-4">The Oracle</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F4EFE6] font-light leading-tight">
              A whisper for
              <br />
              {reading?.date || "today"}.
            </h1>
          </div>
          {moon && (
            <div data-testid={ORACLE.moonPhase} className="text-right">
              <div className="font-overline mb-2">Moon</div>
              <div className="font-heading text-3xl text-[#F4EFE6]/90">{moon.phase_name}</div>
              <div className="font-heading text-5xl text-[#C8A96E] mt-2 breathe">{moon.glyph}</div>
              <div className="font-overline mt-2 text-[#F4EFE6]/40">{moon.illumination}% illumination</div>
            </div>
          )}
        </div>

        {/* Card of the Day */}
        <section data-testid={ORACLE.cardOfDay} className="mb-24">
          <div className="font-overline mb-6 text-[#C8A96E]">Card of the Day</div>

          {loadingReading && (
            <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-12 items-start">
              <div className="aspect-square card-frame breathe" />
              <div className="space-y-4">
                <div className="h-4 w-32 bg-white/5 breathe" />
                <div className="h-6 w-64 bg-white/5 breathe" />
                <div className="h-20 w-full bg-white/5 breathe" />
              </div>
            </div>
          )}

          {error && !loadingReading && (
            <div className="font-heading text-lg text-[#F4EFE6]/70">
              {error}
              <div className="mt-6">
                <button className="btn-sacred" onClick={loadCardOfDay} data-testid={ORACLE.regenerateBtn}>
                  Draw again
                </button>
              </div>
            </div>
          )}

          <AnimatePresence>
            {reading && !loadingReading && (
              <motion.div
                key={reading.date}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.4, ease: "easeInOut" }}
                className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-10 lg:gap-16 items-start"
              >
                <div className="w-full max-w-sm mx-auto lg:mx-0" style={{ perspective: "1600px" }}>
                  <motion.div
                    animate={{ rotateY: flipped ? 180 : 0 }}
                    transition={{ duration: 1.6, ease: [0.25, 0.6, 0.3, 1] }}
                    className="relative w-full aspect-square rounded-2xl cursor-pointer"
                    onClick={() => {
                      if (!flipped) {
                        setFlipped(true);
                        playAudioLogo({ muted });
                      }
                    }}
                    style={{
                      transformStyle: "preserve-3d",
                      boxShadow: "0 22px 60px rgba(0,0,0,0.55)",
                      willChange: "transform",
                      WebkitBackfaceVisibility: "hidden",
                      backfaceVisibility: "hidden",
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-2xl overflow-hidden"
                      style={{ backfaceVisibility: "hidden" }}
                    >
                      <img
                        src={house.back}
                        alt={`${element} House card back`}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    </div>
                    <div
                      className="absolute inset-0 rounded-2xl overflow-hidden bg-black"
                      style={{
                        transform: "rotateY(180deg)",
                        backfaceVisibility: "hidden",
                      }}
                    >
                      <img
                        src={mediaUrl(reading.card.image_url)}
                        alt={reading.card.title}
                        className="w-full h-full object-cover"
                        style={{ transform: reading.card.upright ? "none" : "rotate(180deg)" }}
                        draggable={false}
                      />
                    </div>
                  </motion.div>
                  <div className="pt-4 flex items-center justify-between">
                    <div
                      data-testid={ORACLE.cardTitle}
                      className="font-heading italic text-xl text-[#F4EFE6]"
                    >
                      {reading.card.title}
                    </div>
                    <div className="font-overline text-[#C8A96E]/80">
                      {reading.card.upright ? "Upright" : "Reversed"}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-8">
                    <div className="font-overline mb-2">Signal</div>
                    <NarratedText
                      id="oracle-signal"
                      text={reading.signal}
                      as="p"
                      className="font-heading text-2xl text-[#F4EFE6]/95 leading-snug font-light"
                      manual
                    />
                  </div>
                  <div className="mb-8">
                    <div className="font-overline mb-2">Movement</div>
                    <NarratedText
                      id="oracle-movement"
                      text={reading.movement}
                      as="p"
                      className="text-base text-[#F4EFE6]/75 leading-relaxed"
                      manual
                    />
                  </div>
                  <div className="pt-6 border-t border-[#C8A96E]/15">
                    <div className="font-overline mb-2">A blessing</div>
                    <NarratedText
                      id="oracle-blessing"
                      text={reading.blessing}
                      as="p"
                      className="font-heading text-xl italic text-[#C8A96E]"
                      manual
                    />
                  </div>
                  <div className="mt-8">
                    <button
                      data-testid={ORACLE.saveToJournalBtn}
                      className="btn-sacred"
                      onClick={saveToJournal}
                      disabled={saved}
                    >
                      <Bookmark size={14} strokeWidth={1.25} />
                      {saved ? "Kept in journal" : "Keep in journal"}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>

        {/* Spreads */}
        <section>
          <div className="font-overline mb-6">Spreads</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {spreads.map((s) => (
              <button
                key={s.id}
                data-testid={ORACLE.spreadTile(s.id)}
                onClick={() => {
                  if (s.id === "card-of-day") return;
                  navigate(`/app/spread/${s.id}`);
                }}
                className="text-left card-frame surface-grain p-6 relative flex flex-col justify-between min-h-[180px] transition-all hover:border-[#C8A96E]/50"
              >
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <div className="font-heading italic text-2xl text-[#F4EFE6]">{s.name}</div>
                    {s.premium && (
                      <div className="font-overline text-[#C8A96E] flex items-center gap-1">
                        <Sparkles size={12} strokeWidth={1.5} />
                        Premium
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-[#F4EFE6]/60 leading-relaxed">{s.description}</p>
                </div>
                <div className="mt-6 font-overline text-[#F4EFE6]/40">
                  {s.positions} {s.positions === 1 ? "position" : "positions"}
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
